import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import multiMonthPlugin from '@fullcalendar/multimonth';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import Holidays from 'date-holidays';
import './ResourceCalendar.css';
import { BLOCK_COLORS, BLOCK_NAMES, BLOCK_TYPES, CALENDAR_CONFIG } from './constants';
import { useResourceCalendar } from './useResourceCalendar';

const HOLIDAY_SETTINGS_KEY = 'resourceCalendarHolidaySettings';
const NON_WORKING_HOLIDAY_TYPES = new Set(['public', 'bank', 'school']);
const DEFAULT_HOLIDAY_SETTINGS = {
  country: 'DE',
  state: '',
  region: '',
  showHolidays: true,
  includeObservances: false,
  markNonWorkingDays: true,
};

const toLocalDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseLocalDateInput = (value) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const buildLocalDateTime = (date, time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
    0,
    0,
  );
};

const buildEndDateTime = (date, startTime, endTime) => {
  const startDate = buildLocalDateTime(date, startTime);
  const endDate = buildLocalDateTime(date, endTime);
  if (endDate > startDate) return endDate;
  return new Date(endDate.getTime() + (24 * 60 * 60 * 1000));
};

const toTimeString = (date) =>
  `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

const clampEditorPosition = (left, top) => {
  const width = 280;
  const height = 320;
  const padding = 12;

  return {
    left: Math.min(Math.max(left, padding), window.innerWidth - width - padding),
    top: Math.min(Math.max(top, padding), window.innerHeight - height - padding),
  };
};

const TIME_OPTIONS = Array.from({ length: 96 }, (_, index) => {
  const totalMinutes = index * 15;
  const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
  const minutes = String(totalMinutes % 60).padStart(2, '0');
  return `${hours}:${minutes}`;
});

const getDateParts = (value) => {
  const [year, month, day] = value.split('-').map(Number);
  return { year, month, day };
};

const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

const buildMonthOptions = () => Array.from({ length: 12 }, (_, index) => ({
  value: String(index + 1).padStart(2, '0'),
  label: new Date(2026, index, 1).toLocaleDateString('en-US', { month: 'long' }),
}));

const buildYearOptions = (anchorYear) => Array.from({ length: 11 }, (_, index) => {
  const value = String(anchorYear - 5 + index);
  return { value, label: value };
});

const loadHolidaySettings = () => {
  try {
    const raw = localStorage.getItem(HOLIDAY_SETTINGS_KEY);
    return raw ? { ...DEFAULT_HOLIDAY_SETTINGS, ...JSON.parse(raw) } : DEFAULT_HOLIDAY_SETTINGS;
  } catch {
    return DEFAULT_HOLIDAY_SETTINGS;
  }
};

const sortOptions = (entries) => entries
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

const parseHolidayDateKey = (value) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const addDaysToKey = (value, days) => {
  const date = parseHolidayDateKey(value);
  date.setDate(date.getDate() + days);
  return toLocalDateKey(date);
};

class ResourceCalendarErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('ResourceCalendar crashed:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="resource-calendar">
          <div className="resource-calendar-instructions">
            <p>Resource Calendar failed to render.</p>
            <pre className="output-preview">{String(this.state.error)}</pre>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const ResourceCalendarContent = ({ onTitleChange, onRegisterSettingsGetter, initialSettings }) => {
  const {
    config,
    getEventsInRange,
    selectedDate,
    normalizedOutput,
    addEvent,
    updateEvent,
    deleteEvent,
    clearAllEvents,
    updateDefaultRule,
    addDefaultRule,
    removeDefaultRule,
    updateOverride,
    selectDate,
    restoreFromBackup,
  } = useResourceCalendar();

  const calendarRef = useRef(null);
  const externalEventsRef = useRef(null);
  const [selectedBlockType, setSelectedBlockType] = useState(BLOCK_TYPES.WORK);
  const [currentMonthYear, setCurrentMonthYear] = useState('');
  const [quickEventTitle, setQuickEventTitle] = useState('Manual Event');
  const [quickEventDetails, setQuickEventDetails] = useState('');
  const [quickEventDateInput, setQuickEventDateInput] = useState(toLocalDateKey(new Date()));
  const [quickEventStartTime, setQuickEventStartTime] = useState('09:00');
  const [quickEventEndTime, setQuickEventEndTime] = useState('10:00');
  const [editingEvent, setEditingEvent] = useState(null);
  const [editorDrag, setEditorDrag] = useState(null);
  const [armedDateClickKey, setArmedDateClickKey] = useState(null);
  const [holidaySettings, setHolidaySettings] = useState(() => loadHolidaySettings());
  const [componentTitle, setComponentTitle] = useState('Resource Calendar');

  // Restore settings from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.componentTitle !== undefined) setComponentTitle(initialSettings.componentTitle);
  }, []);

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({ componentTitle }));
  }, [componentTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live-sync the tile title to the parent (GridComponent header)
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(componentTitle);
  }, [componentTitle]);
  const [currentRange, setCurrentRange] = useState(() => ({
    start: new Date(),
    end: new Date(new Date().getTime() + (31 * 24 * 60 * 60 * 1000)),
  }));
  const [externalEvents, setExternalEvents] = useState([
    {
      id: 'template-work-default',
      title: 'Default Work Block',
      details: 'Standard work block',
      type: BLOCK_TYPES.WORK,
      startTime: '09:00',
      endTime: '17:00',
    },
    {
      id: 'template-break-default',
      title: 'Default Break Block',
      details: 'Midday break',
      type: BLOCK_TYPES.BREAK,
      startTime: '12:00',
      endTime: '12:30',
    },
  ]);

  useEffect(() => {
    if (!externalEventsRef.current) return undefined;

    const draggable = new Draggable(externalEventsRef.current, {
      itemSelector: '.external-event-item',
      appendTo: typeof document !== 'undefined' ? document.body : undefined,
      minDistance: 0,
      longPressDelay: 0,
    });

    return () => draggable.destroy();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(HOLIDAY_SETTINGS_KEY, JSON.stringify(holidaySettings));
    } catch (error) {
      console.warn('Failed to save holiday settings', error);
    }
  }, [holidaySettings]);

  const holidayCatalog = useMemo(() => new Holidays(), []);

  const countryOptions = useMemo(
    () => sortOptions(Object.entries(holidayCatalog.getCountries() || {})),
    [holidayCatalog],
  );

  const stateOptions = useMemo(() => {
    if (!holidaySettings.country) return [];
    return sortOptions(Object.entries(
      holidayCatalog.getStates(holidaySettings.country) || {}
    ));
  }, [holidayCatalog, holidaySettings.country]);

  const regionOptions = useMemo(() => {
    if (!holidaySettings.country || !holidaySettings.state) return [];
    return sortOptions(Object.entries(
      holidayCatalog.getRegions(holidaySettings.country, holidaySettings.state) || {}
    ));
  }, [holidayCatalog, holidaySettings.country, holidaySettings.state]);

  useEffect(() => {
    if (!holidaySettings.country) return;
    if (stateOptions.length === 0 && holidaySettings.state) {
      setHolidaySettings((prev) => ({ ...prev, state: '', region: '' }));
      return;
    }
    if (holidaySettings.state && !stateOptions.some((option) => option.value === holidaySettings.state)) {
      setHolidaySettings((prev) => ({ ...prev, state: '', region: '' }));
    }
  }, [holidaySettings.country, holidaySettings.state, stateOptions]);

  useEffect(() => {
    if (regionOptions.length === 0 && holidaySettings.region) {
      setHolidaySettings((prev) => ({ ...prev, region: '' }));
      return;
    }
    if (holidaySettings.region && !regionOptions.some((option) => option.value === holidaySettings.region)) {
      setHolidaySettings((prev) => ({ ...prev, region: '' }));
    }
  }, [holidaySettings.region, regionOptions]);

  const holidayEngine = useMemo(() => {
    if (!holidaySettings.country) return null;
    try {
      const hd = new Holidays();
      if (holidaySettings.country && holidaySettings.state && holidaySettings.region) {
        hd.init(holidaySettings.country, holidaySettings.state, holidaySettings.region);
      } else if (holidaySettings.country && holidaySettings.state) {
        hd.init(holidaySettings.country, holidaySettings.state);
      } else {
        hd.init(holidaySettings.country);
      }
      return hd;
    } catch (error) {
      console.warn('Failed to initialize holiday data', error);
      return null;
    }
  }, [holidaySettings.country, holidaySettings.region, holidaySettings.state]);

  const buildHolidayDataInRange = useCallback((start, end) => {
    if (!holidayEngine || !holidaySettings.showHolidays) {
      return { events: [], lookup: new Map() };
    }

    const startYear = start.getFullYear();
    const endYear = end.getFullYear();
    const items = [];

    for (let year = startYear; year <= endYear; year += 1) {
      items.push(...holidayEngine.getHolidays(year));
    }

    const lookup = new Map();
    const events = items
      .filter((holiday) => holiday?.date)
      .filter((holiday) => holidaySettings.includeObservances || NON_WORKING_HOLIDAY_TYPES.has(holiday.type))
      .filter((holiday) => {
        const dateKey = holiday.date.slice(0, 10);
        const date = parseHolidayDateKey(dateKey);
        return date >= start && date < end;
      })
      .map((holiday) => {
        const dateKey = holiday.date.slice(0, 10);
        const existing = lookup.get(dateKey) || [];
        existing.push(holiday);
        lookup.set(dateKey, existing);

        return {
          id: `holiday:${holidaySettings.country}:${holidaySettings.state || 'all'}:${holidaySettings.region || 'all'}:${dateKey}:${holiday.name}`,
          title: holiday.name,
          start: dateKey,
          end: addDaysToKey(dateKey, 1),
          allDay: true,
          editable: false,
          display: holidaySettings.markNonWorkingDays ? 'background' : 'auto',
          backgroundColor: holiday.type === 'observance' ? '#fbbf24' : '#fecaca',
          borderColor: holiday.type === 'observance' ? '#f59e0b' : '#ef4444',
          classNames: [
            'holiday-event',
            holidaySettings.markNonWorkingDays ? 'holiday-background-event' : 'holiday-foreground-event',
          ],
          extendedProps: {
            isHoliday: true,
            holidayName: holiday.name,
            holidayType: holiday.type,
            dateKey,
          },
        };
      });

    return { events, lookup };
  }, [holidayEngine, holidaySettings.includeObservances, holidaySettings.markNonWorkingDays, holidaySettings.showHolidays, holidaySettings.country, holidaySettings.region, holidaySettings.state]);

  const holidayDataForView = useMemo(
    () => buildHolidayDataInRange(currentRange.start, currentRange.end),
    [buildHolidayDataInRange, currentRange.end, currentRange.start],
  );

  const holidayLookup = holidayDataForView.lookup;

  const selectedDateKey = useMemo(
    () => (selectedDate ? toLocalDateKey(selectedDate) : null),
    [selectedDate],
  );

  const selectedOverride = selectedDateKey ? config.overrides[selectedDateKey] : null;
  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const quickEventDateParts = useMemo(() => getDateParts(quickEventDateInput), [quickEventDateInput]);
  const quickYearOptions = useMemo(() => buildYearOptions(quickEventDateParts.year), [quickEventDateParts.year]);
  const quickDayOptions = useMemo(() => (
    Array.from({ length: getDaysInMonth(quickEventDateParts.year, quickEventDateParts.month) }, (_, index) => {
      const value = String(index + 1).padStart(2, '0');
      return { value, label: value };
    })
  ), [quickEventDateParts.month, quickEventDateParts.year]);
  const editingDateParts = useMemo(
    () => (editingEvent ? getDateParts(editingEvent.date) : null),
    [editingEvent],
  );
  const editingYearOptions = useMemo(
    () => buildYearOptions(editingDateParts?.year ?? new Date().getFullYear()),
    [editingDateParts?.year],
  );
  const editingDayOptions = useMemo(() => {
    if (!editingDateParts) return [];
    return Array.from({ length: getDaysInMonth(editingDateParts.year, editingDateParts.month) }, (_, index) => {
      const value = String(index + 1).padStart(2, '0');
      return { value, label: value };
    });
  }, [editingDateParts]);

  const eventSourceFunction = useCallback(
    (fetchInfo, successCallback) => {
      const holidayData = buildHolidayDataInRange(fetchInfo.start, fetchInfo.end);
      successCallback([
        ...getEventsInRange(fetchInfo.start, fetchInfo.end),
        ...holidayData.events,
      ]);
    },
    [buildHolidayDataInRange, getEventsInRange],
  );

  const handleHolidaySettingChange = useCallback((field, value) => {
    setHolidaySettings((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateDateKeyPart = useCallback((currentValue, part, nextValue) => {
    const parts = getDateParts(currentValue);
    const nextParts = { ...parts };

    if (part === 'year') nextParts.year = Number(nextValue);
    if (part === 'month') nextParts.month = Number(nextValue);
    if (part === 'day') nextParts.day = Number(nextValue);

    const maxDay = getDaysInMonth(nextParts.year, nextParts.month);
    nextParts.day = Math.min(nextParts.day, maxDay);

    return `${String(nextParts.year)}-${String(nextParts.month).padStart(2, '0')}-${String(nextParts.day).padStart(2, '0')}`;
  }, []);

  const handleQuickDatePartChange = useCallback((part, value) => {
    const nextDateKey = updateDateKeyPart(quickEventDateInput, part, value);
    setQuickEventDateInput(nextDateKey);
    selectDate(parseLocalDateInput(nextDateKey));
  }, [quickEventDateInput, selectDate, updateDateKeyPart]);

  const renderDayHeader = useCallback((info) => {
    const viewType = info.view.type;
    const dateKey = toLocalDateKey(info.date);
    const holidayItems = holidayLookup.get(dateKey) || [];
    const holidayText = holidayItems.map((item) => item.name).join(', ');

    let label = info.date.toLocaleDateString('en-US', { weekday: 'short' });
    if (viewType === 'timeGridDay') {
      label = info.date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    } else if (viewType === 'timeGridWeek' || viewType === 'listWeek') {
      label = info.date.toLocaleDateString('en-US', {
        weekday: 'short',
        day: '2-digit',
      });
    }

    return (
      <div className="fc-day-header-stack">
        <span>{label}</span>
        {holidayText ? <small className="fc-day-header-holiday">{holidayText}</small> : null}
      </div>
    );
  }, [holidayLookup]);

  const handleSelect = useCallback(
    (selectInfo) => {
      selectInfo.view.calendar.unselect();
      const start = new Date(selectInfo.start);
      const end = new Date(selectInfo.end);
      const nextDateKey = toLocalDateKey(start);
      const nextStartTime = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
      const nextEndTime = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;

      selectDate(start);
      setArmedDateClickKey(null);
      setQuickEventDateInput(nextDateKey);
      setQuickEventStartTime(nextStartTime);
      setQuickEventEndTime(nextEndTime);

      addEvent({
        title: quickEventTitle.trim() || BLOCK_NAMES[selectedBlockType],
        details: quickEventDetails.trim(),
        start,
        end,
        allDay: false,
        type: selectedBlockType,
      });
    },
    [addEvent, quickEventDetails, quickEventTitle, selectDate, selectedBlockType],
  );

  const toExternalEventData = useCallback(
    (item) => JSON.stringify({
      title: item.title,
      startTime: item.startTime,
      duration: '01:00',
      create: false,
      extendedProps: {
        type: item.type,
        details: item.details,
        templateStartTime: item.startTime,
        templateEndTime: item.endTime,
      },
    }),
    [],
  );

  const handleEventChange = useCallback(
    (changeInfo) => {
      const { event } = changeInfo;
      if (event.extendedProps?.isHoliday) return;
      updateEvent(
        event.id,
        {
          title: event.title,
          details: event.extendedProps?.details || '',
          type: event.extendedProps?.type || BLOCK_TYPES.WORK,
          start: event.startStr,
          end: event.endStr || event.startStr,
          allDay: event.allDay,
        },
        event.extendedProps,
      );
    },
    [updateEvent],
  );

  useEffect(() => {
    if (!editorDrag) return undefined;

    const handlePointerMove = (event) => {
      setEditingEvent((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          position: clampEditorPosition(
            event.clientX - editorDrag.offsetX,
            event.clientY - editorDrag.offsetY,
          ),
        };
      });
    };

    const stopDragging = () => setEditorDrag(null);

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', stopDragging);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', stopDragging);
    };
  }, [editorDrag]);

  const openEventEditor = useCallback((event, jsEvent) => {
    const eventDate = event.start || new Date();
    const eventEnd = event.end || event.start || new Date();
    const position = clampEditorPosition(
      (jsEvent?.clientX || 24) + 12,
      (jsEvent?.clientY || 24) + 12,
    );

    setEditingEvent({
      id: event.id,
      itemId: event.extendedProps?.itemId || event.id,
      dateKey: event.extendedProps?.dateKey || toLocalDateKey(eventDate),
      source: event.extendedProps?.source || 'override',
      title: event.title || '',
      details: event.extendedProps?.details || '',
      type: event.extendedProps?.type || BLOCK_TYPES.WORK,
      date: toLocalDateKey(eventDate),
      startTime: toTimeString(eventDate),
      endTime: toTimeString(eventEnd),
      position,
    });

    setQuickEventTitle(event.title || '');
    setQuickEventDetails(event.extendedProps?.details || '');
    setSelectedBlockType(event.extendedProps?.type || BLOCK_TYPES.WORK);
    setQuickEventDateInput(toLocalDateKey(eventDate));
    setQuickEventStartTime(toTimeString(eventDate));
    setQuickEventEndTime(toTimeString(eventEnd));
    selectDate(eventDate);
  }, []);

  const handleEventClick = useCallback(
    (clickInfo) => {
      if (clickInfo.event.extendedProps?.isHoliday) {
        clickInfo.jsEvent.preventDefault();
        return;
      }
      clickInfo.jsEvent.preventDefault();
      openEventEditor(clickInfo.event, clickInfo.jsEvent);
    },
    [openEventEditor],
  );

  const handleDateClick = useCallback(
    (info) => {
      const dateKey = toLocalDateKey(info.date);

      if (armedDateClickKey === dateKey) {
        const startDate = buildLocalDateTime(info.date, quickEventStartTime);
        const endDate = buildEndDateTime(info.date, quickEventStartTime, quickEventEndTime);

        addEvent({
          title: quickEventTitle.trim() || BLOCK_NAMES[selectedBlockType],
          details: quickEventDetails.trim(),
          start: startDate,
          end: endDate,
          allDay: false,
          type: selectedBlockType,
        });
        setArmedDateClickKey(null);
        return;
      }

      setArmedDateClickKey(dateKey);
      selectDate(info.date);
      setQuickEventDateInput(dateKey);
    },
    [addEvent, armedDateClickKey, quickEventDetails, quickEventEndTime, quickEventStartTime, quickEventTitle, selectDate, selectedBlockType],
  );

  const handleDatesSet = useCallback((info) => {
    setCurrentRange({ start: new Date(info.start), end: new Date(info.end) });
    setCurrentMonthYear(
      info.view.currentStart.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      }),
    );
  }, []);

  const handleExternalDrop = useCallback(
    (dropInfo) => {
      const raw = dropInfo.draggedEl?.getAttribute('data-event');
      if (!raw) return;

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return;
      }

      const type = parsed.extendedProps?.type || BLOCK_TYPES.WORK;
      const title = parsed.title || BLOCK_NAMES[type];
      const templateStartTime = parsed.extendedProps?.templateStartTime || parsed.startTime || '09:00';
      const templateEndTime = parsed.extendedProps?.templateEndTime || '10:00';
      const baseDate = dropInfo.date || new Date();
      const startDate = buildLocalDateTime(baseDate, templateStartTime);
      const endDate = buildEndDateTime(baseDate, templateStartTime, templateEndTime);

      addEvent({
        title,
        details: parsed.extendedProps?.details || '',
        start: startDate,
        end: endDate,
        allDay: false,
        type,
      });
    },
    [addEvent],
  );

  const updateRuleInterval = useCallback(
    (rule, index, field, value) => {
      updateDefaultRule(rule.id, {
        intervals: rule.intervals.map((iv, idx) =>
          idx === index ? { ...iv, [field]: value } : iv
        ),
      });
    },
    [updateDefaultRule],
  );

  const addRuleInterval = useCallback(
    (rule) => {
      updateDefaultRule(rule.id, {
        intervals: [...rule.intervals, { start: '09:00', end: '17:00', type: BLOCK_TYPES.WORK }],
      });
    },
    [updateDefaultRule],
  );

  const removeRuleInterval = useCallback(
    (rule, index) => {
      updateDefaultRule(rule.id, {
        intervals: rule.intervals.filter((_, idx) => idx !== index),
      });
    },
    [updateDefaultRule],
  );

  const toggleRuleDay = useCallback(
    (rule, day) => {
      const days = rule.daysOfWeek.includes(day)
        ? rule.daysOfWeek.filter((value) => value !== day)
        : [...rule.daysOfWeek, day].sort((a, b) => a - b);

      updateDefaultRule(rule.id, { daysOfWeek: days });
    },
    [updateDefaultRule],
  );

  const updateOverrideIntervals = useCallback(
    (intervals) => {
      if (!selectedDateKey) return;
      updateOverride(selectedDateKey, { enabled: true, intervals });
    },
    [selectedDateKey, updateOverride],
  );

  const addOverrideInterval = useCallback(() => {
    updateOverrideIntervals([
      ...(selectedOverride?.intervals || []),
      { start: '09:00', end: '17:00', type: BLOCK_TYPES.WORK },
    ]);
  }, [selectedOverride, updateOverrideIntervals]);

  const updateOverrideField = useCallback(
    (index, field, value) => {
      updateOverrideIntervals(
        (selectedOverride?.intervals || []).map((iv, idx) =>
          idx === index ? { ...iv, [field]: value } : iv
        ),
      );
    },
    [selectedOverride, updateOverrideIntervals],
  );

  const removeOverrideInterval = useCallback(
    (index) => {
      updateOverrideIntervals(
        (selectedOverride?.intervals || []).filter((_, idx) => idx !== index),
      );
    },
    [selectedOverride, updateOverrideIntervals],
  );

  const handleJumpToday = useCallback(() => {
    calendarRef.current?.getApi()?.today();
  }, []);

  const handleGoToDate = useCallback(() => {
    if (!selectedDateKey) return;
    calendarRef.current?.getApi()?.gotoDate(selectedDateKey);
  }, [selectedDateKey]);

  const handleCreateEvent = useCallback(() => {
    const formDate = parseLocalDateInput(quickEventDateInput);
    const startDate = buildLocalDateTime(formDate, quickEventStartTime);
    const endDate = buildEndDateTime(formDate, quickEventStartTime, quickEventEndTime);
    selectDate(formDate);
    setArmedDateClickKey(null);

    addEvent({
      title: quickEventTitle.trim() || BLOCK_NAMES[selectedBlockType],
      details: quickEventDetails.trim(),
      start: startDate,
      end: endDate,
      allDay: false,
      type: selectedBlockType,
    });
  }, [
    addEvent,
    quickEventDetails,
    quickEventDateInput,
    quickEventEndTime,
    quickEventStartTime,
    quickEventTitle,
    selectDate,
    selectedBlockType,
  ]);

  const handleSaveDragTemplate = useCallback(() => {
    const title = quickEventTitle.trim() || BLOCK_NAMES[selectedBlockType];

    setExternalEvents((prev) => [
      {
        id: `template-${Date.now()}`,
        title,
        details: quickEventDetails.trim(),
        type: selectedBlockType,
        startTime: quickEventStartTime,
        endTime: quickEventEndTime,
      },
      ...prev,
    ]);
  }, [
    quickEventDetails,
    quickEventEndTime,
    quickEventStartTime,
    quickEventTitle,
    selectedBlockType,
  ]);

  const handleLoadTemplate = useCallback((template) => {
    setQuickEventTitle(template.title);
    setQuickEventDetails(template.details || '');
    setSelectedBlockType(template.type);
    setQuickEventStartTime(template.startTime);
    setQuickEventEndTime(template.endTime);
  }, []);

  const handleRemoveTemplate = useCallback((templateId) => {
    setExternalEvents((prev) => prev.filter((item) => item.id !== templateId));
  }, []);

  const handleEditorChange = useCallback((field, value) => {
    setEditingEvent((prev) => (prev ? { ...prev, [field]: value } : prev));
  }, []);

  const handleEditingDatePartChange = useCallback((part, value) => {
    setEditingEvent((prev) => {
      if (!prev) return prev;
      return { ...prev, date: updateDateKeyPart(prev.date, part, value) };
    });
  }, [updateDateKeyPart]);

  const handleSaveEditedEvent = useCallback(() => {
    if (!editingEvent) return;
    const editDate = parseLocalDateInput(editingEvent.date);
    const startDate = buildLocalDateTime(editDate, editingEvent.startTime);
    const endDate = buildEndDateTime(editDate, editingEvent.startTime, editingEvent.endTime);

    updateEvent(
      editingEvent.id,
      {
        title: editingEvent.title,
        details: editingEvent.details,
        type: editingEvent.type,
        start: startDate,
        end: endDate,
        allDay: false,
      },
      {
        itemId: editingEvent.itemId,
        dateKey: editingEvent.dateKey,
        source: editingEvent.source,
        type: editingEvent.type,
        title: editingEvent.title,
        details: editingEvent.details,
      },
    );
    setEditingEvent(null);
    setArmedDateClickKey(null);
  }, [editingEvent, updateEvent]);

  const handleDeleteEditedEvent = useCallback(() => {
    if (!editingEvent) return;
    deleteEvent(editingEvent.id, {
      itemId: editingEvent.itemId,
      dateKey: editingEvent.dateKey,
      source: editingEvent.source,
    });
    setEditingEvent(null);
    setArmedDateClickKey(null);
  }, [deleteEvent, editingEvent]);

  const handleExport = useCallback(() => {
    const data = JSON.stringify(normalizedOutput, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'resource-calendar-output.json';
    link.click();
    URL.revokeObjectURL(url);
  }, [normalizedOutput]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(normalizedOutput, null, 2));
    } catch (error) {
      console.warn('Failed to copy normalized output', error);
    }
  }, [normalizedOutput]);

  const renderEventContent = useCallback((eventInfo) => (
    <div className="calendar-event-content">
      <div className="calendar-event-content-line">
        <strong>{eventInfo.timeText}</strong>{' '}
        <span>{eventInfo.event.title}</span>
      </div>
      {eventInfo.event.extendedProps?.details ? (
        <div className="calendar-event-content-details">
          {eventInfo.event.extendedProps.details}
        </div>
      ) : null}
    </div>
  ), []);

  return (
    <div className="resource-calendar">
      <div className="resource-calendar-layout">
        <aside className="resource-calendar-sidebar">
          <div className="resource-calendar-panel">
            <h4>Tile Title</h4>
            <input
              type="text"
              className="quick-event-input"
              value={componentTitle}
              onChange={(e) => setComponentTitle(e.target.value)}
              placeholder="Resource Calendar"
            />
          </div>

          <h3 className="sidebar-title">Event Setup</h3>

          <div className="resource-calendar-controls">
            <div className="block-type-selector">
              <span className="block-type-label">Block Type:</span>
              <div className="block-type-buttons">
                {Object.values(BLOCK_TYPES).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSelectedBlockType(type)}
                    className={`block-type-button ${selectedBlockType === type ? 'active' : ''}`}
                    style={{
                      backgroundColor: selectedBlockType === type ? BLOCK_COLORS[type] : undefined,
                      color: selectedBlockType === type ? '#ffffff' : undefined,
                      borderColor: selectedBlockType === type ? BLOCK_COLORS[type] : undefined,
                    }}
                  >
                    {BLOCK_NAMES[type]}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={() => { if (window.confirm('Clear all events?')) clearAllEvents(); }}
              className="clear-all-button"
            >
              Clear All
            </button>
            <button type="button" onClick={restoreFromBackup} className="backup-button">
              Restore Backup
            </button>
          </div>

          <div className="resource-calendar-panel">
            <h4>Create Event</h4>
            <p className="panel-subtitle">
              Use one form for everything. Create directly, or save the same setup as a draggable FullCalendar template.
            </p>
            <div className="quick-event-form">
              <div className="quick-event-row">
                <label className="quick-event-label">Title</label>
                <input
                  type="text"
                  placeholder="Event title"
                  value={quickEventTitle}
                  onChange={(e) => setQuickEventTitle(e.target.value)}
                  className="quick-event-input"
                />
              </div>
              <div className="quick-event-row">
                <label className="quick-event-label">Details</label>
                <input
                  type="text"
                  placeholder="Optional details"
                  value={quickEventDetails}
                  onChange={(e) => setQuickEventDetails(e.target.value)}
                  className="quick-event-input"
                />
              </div>
              <div className="quick-event-row">
                <label className="quick-event-label">Type</label>
                <select
                  value={selectedBlockType}
                  onChange={(e) => setSelectedBlockType(e.target.value)}
                  className="quick-event-select"
                >
                  <option value={BLOCK_TYPES.WORK}>Work</option>
                  <option value={BLOCK_TYPES.BREAK}>Break</option>
                </select>
              </div>
              <div className="quick-event-row">
                <label className="quick-event-label">Date</label>
                <div className="quick-event-date-fields">
                  <select
                    value={String(quickEventDateParts.day).padStart(2, '0')}
                    onChange={(e) => handleQuickDatePartChange('day', e.target.value)}
                    className="quick-event-select"
                  >
                    {quickDayOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={String(quickEventDateParts.month).padStart(2, '0')}
                    onChange={(e) => handleQuickDatePartChange('month', e.target.value)}
                    className="quick-event-select"
                  >
                    {monthOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={String(quickEventDateParts.year)}
                    onChange={(e) => handleQuickDatePartChange('year', e.target.value)}
                    className="quick-event-select"
                  >
                    {quickYearOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="quick-event-row">
                <label className="quick-event-label">Start Time</label>
                <select
                  value={quickEventStartTime}
                  onChange={(e) => setQuickEventStartTime(e.target.value)}
                  className="quick-event-select"
                >
                  {TIME_OPTIONS.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
              <div className="quick-event-row">
                <label className="quick-event-label">End Time</label>
                <select
                  value={quickEventEndTime}
                  onChange={(e) => setQuickEventEndTime(e.target.value)}
                  className="quick-event-select"
                >
                  {TIME_OPTIONS.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
              <div className="quick-event-actions">
                <button
                  type="button"
                  className="rule-add"
                  onClick={handleSaveDragTemplate}
                >
                  Save As Drag Template
                </button>
                <button
                  type="button"
                  className="quick-event-create"
                  onClick={handleCreateEvent}
                >
                  Create Event
                </button>
              </div>
            </div>
          </div>

          <div className="resource-calendar-panel">
            <h4>Drag Templates</h4>
            <p className="panel-subtitle">
              Drag any template into the calendar. Click one to load it back into the form for editing.
            </p>
            <div className="sidebar-section external-events-wrapper" ref={externalEventsRef}>
              {externalEvents.map((item) => (
                <div key={item.id} className="external-event-row">
                  <button
                    type="button"
                    className="sidebar-pill external-event-item fc-event fc-h-event"
                    data-event={toExternalEventData(item)}
                    style={{ backgroundColor: BLOCK_COLORS[item.type] }}
                    onClick={() => handleLoadTemplate(item)}
                  >
                    <div>{item.title}</div>
                    <small>{item.startTime} - {item.endTime}</small>
                    {item.details ? <small>{item.details}</small> : null}
                  </button>
                  <button
                    type="button"
                    className="external-remove-button"
                    onClick={() => handleRemoveTemplate(item.id)}
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="resource-calendar-panels">
            <div className="resource-calendar-panel">
              <h4>Default Rules</h4>
              {config.defaultRules.map((rule) => (
                <div key={rule.id} className="rule-card">
                  <div className="rule-header">
                    <input
                      className="rule-name-input"
                      value={rule.name}
                      onChange={(e) => updateDefaultRule(rule.id, { name: e.target.value })}
                    />
                    <button
                      type="button"
                      className="rule-remove"
                      onClick={() => removeDefaultRule(rule.id)}
                    >
                      Remove
                    </button>
                  </div>

                  <div className="rule-days">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, dayIndex) => (
                      <button
                        key={`${rule.id}-day-${dayIndex}`}
                        type="button"
                        className={`day-button ${rule.daysOfWeek.includes(dayIndex) ? 'active' : ''}`}
                        onClick={() => toggleRuleDay(rule, dayIndex)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="interval-list">
                    {rule.intervals.map((interval, index) => (
                      <div key={`${rule.id}-interval-${index}`} className="interval-row">
                        <input
                          type="time"
                          value={interval.start}
                          onChange={(e) => updateRuleInterval(rule, index, 'start', e.target.value)}
                        />
                        <input
                          type="time"
                          value={interval.end}
                          onChange={(e) => updateRuleInterval(rule, index, 'end', e.target.value)}
                        />
                        <select
                          value={interval.type}
                          onChange={(e) => updateRuleInterval(rule, index, 'type', e.target.value)}
                        >
                          <option value={BLOCK_TYPES.WORK}>Work</option>
                          <option value={BLOCK_TYPES.BREAK}>Break</option>
                        </select>
                        <button
                          type="button"
                          className="rule-remove"
                          onClick={() => removeRuleInterval(rule, index)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button type="button" className="rule-add" onClick={() => addRuleInterval(rule)}>
                      Add Interval
                    </button>
                  </div>
                </div>
              ))}
              <button type="button" className="rule-add" onClick={addDefaultRule}>
                Add Rule
              </button>
            </div>

            <div className="resource-calendar-panel">
              <h4>Holiday Region</h4>
              <p className="panel-subtitle">
                Select country/state/region and show local holidays as non-working days in the calendar.
              </p>
              <div className="holiday-settings-grid">
                <label className="holiday-settings-field">
                  <span>Country</span>
                  <select
                    value={holidaySettings.country}
                    onChange={(e) => setHolidaySettings((prev) => ({
                      ...prev,
                      country: e.target.value,
                      state: '',
                      region: '',
                    }))}
                  >
                    {countryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="holiday-settings-field">
                  <span>State / Province</span>
                  <select
                    value={holidaySettings.state}
                    onChange={(e) => setHolidaySettings((prev) => ({
                      ...prev,
                      state: e.target.value,
                      region: '',
                    }))}
                    disabled={stateOptions.length === 0}
                  >
                    <option value="">All</option>
                    {stateOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="holiday-settings-field">
                  <span>Region</span>
                  <select
                    value={holidaySettings.region}
                    onChange={(e) => handleHolidaySettingChange('region', e.target.value)}
                    disabled={regionOptions.length === 0}
                  >
                    <option value="">All</option>
                    {regionOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="override-toggle">
                <input
                  type="checkbox"
                  checked={holidaySettings.showHolidays}
                  onChange={(e) => handleHolidaySettingChange('showHolidays', e.target.checked)}
                />
                Show holidays in calendar
              </label>
              <label className="override-toggle">
                <input
                  type="checkbox"
                  checked={holidaySettings.markNonWorkingDays}
                  onChange={(e) => handleHolidaySettingChange('markNonWorkingDays', e.target.checked)}
                />
                Mark public holidays as non-working days
              </label>
              <label className="override-toggle">
                <input
                  type="checkbox"
                  checked={holidaySettings.includeObservances}
                  onChange={(e) => handleHolidaySettingChange('includeObservances', e.target.checked)}
                />
                Include observances in holiday list
              </label>
            </div>

            <div className="resource-calendar-panel">
              <h4>Date Override</h4>
              <p className="panel-subtitle">
                {selectedDateKey ? `Selected: ${selectedDateKey}` : 'Select a date in the calendar.'}
              </p>

              {selectedDateKey && (
                <>
                  <label className="override-toggle">
                    <input
                      type="checkbox"
                      checked={selectedOverride?.enabled ?? true}
                      onChange={(e) => updateOverride(selectedDateKey, {
                        enabled: e.target.checked,
                        intervals: selectedOverride?.intervals || [],
                      })}
                    />
                    Enable override for this date
                  </label>

                  <div className="interval-list">
                    {(selectedOverride?.intervals || []).map((interval, index) => (
                      <div key={`override-${index}`} className="interval-row">
                        <input
                          type="time"
                          value={interval.start}
                          onChange={(e) => updateOverrideField(index, 'start', e.target.value)}
                        />
                        <input
                          type="time"
                          value={interval.end}
                          onChange={(e) => updateOverrideField(index, 'end', e.target.value)}
                        />
                        <select
                          value={interval.type}
                          onChange={(e) => updateOverrideField(index, 'type', e.target.value)}
                        >
                          <option value={BLOCK_TYPES.WORK}>Work</option>
                          <option value={BLOCK_TYPES.BREAK}>Break</option>
                        </select>
                        <button
                          type="button"
                          className="rule-remove"
                          onClick={() => removeOverrideInterval(index)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button type="button" className="rule-add" onClick={addOverrideInterval}>
                      Add Override Interval
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="resource-calendar-panel">
              <h4>Normalized Output</h4>
              <div className="output-actions">
                <button type="button" onClick={handleExport} className="rule-add">Export JSON</button>
                <button type="button" onClick={handleCopy} className="rule-add">Copy JSON</button>
              </div>
              <pre className="output-preview">
                {JSON.stringify(normalizedOutput, null, 2)}
              </pre>
            </div>
          </div>
        </aside>

        <main className="resource-calendar-main">
          <div className="resource-calendar-header">
            <div>
              <h3 className="resource-calendar-title">{componentTitle}</h3>
              {currentMonthYear ? (
                <div className="resource-calendar-month-year">{currentMonthYear}</div>
              ) : null}
            </div>
          </div>

          <div className="resource-calendar-container">
            <FullCalendar
              ref={calendarRef}
              plugins={[
                dayGridPlugin,
                timeGridPlugin,
                listPlugin,
                multiMonthPlugin,
                interactionPlugin,
              ]}
              initialView={CALENDAR_CONFIG.DEFAULT_VIEW}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek,multiMonthYear',
              }}
              customButtons={{
                jumpToday: {
                  text: 'Jump Today',
                  click: handleJumpToday,
                },
                gotoSelected: {
                  text: 'Go To Selected',
                  click: handleGoToDate,
                },
              }}
              footerToolbar={{
                left: 'jumpToday,gotoSelected',
                center: '',
                right: '',
              }}
              selectable={CALENDAR_CONFIG.SELECTABLE}
              editable={CALENDAR_CONFIG.EDITABLE}
              weekends={CALENDAR_CONFIG.WEEKENDS}
              navLinks={CALENDAR_CONFIG.NAV_LINKS}
              weekNumbers={CALENDAR_CONFIG.WEEK_NUMBERS}
              firstDay={CALENDAR_CONFIG.FIRST_DAY}
              nowIndicator={CALENDAR_CONFIG.NOW_INDICATOR}
              businessHours={CALENDAR_CONFIG.BUSINESS_HOURS}
              slotMinTime={CALENDAR_CONFIG.SLOT_MIN_TIME}
              slotMaxTime={CALENDAR_CONFIG.SLOT_MAX_TIME}
              slotDuration={CALENDAR_CONFIG.SLOT_DURATION}
              dayHeaderFormat={CALENDAR_CONFIG.DAY_HEADER_FORMAT}
              dayHeaderContent={renderDayHeader}
              eventTimeFormat={CALENDAR_CONFIG.EVENT_TIME_FORMAT}
              displayEventEnd
              eventOrder="start,-duration,title"
              rerenderDelay={CALENDAR_CONFIG.RERENDER_DELAY}
              windowResizeDelay={CALENDAR_CONFIG.WINDOW_RESIZE_DELAY}
              longPressDelay={CALENDAR_CONFIG.LONG_PRESS_DELAY}
              eventLongPressDelay={CALENDAR_CONFIG.EVENT_LONG_PRESS_DELAY}
              selectLongPressDelay={CALENDAR_CONFIG.SELECT_LONG_PRESS_DELAY}
              lazyFetching
              selectMirror
              dayMaxEvents
              droppable
              defaultAllDay={false}
              defaultAllDayEventDuration={{ days: 1 }}
              defaultTimedEventDuration="01:00"
              forceEventDuration
              events={eventSourceFunction}
              select={handleSelect}
              eventChange={() => {}}
              eventDrop={handleEventChange}
              eventResize={handleEventChange}
              eventClick={handleEventClick}
              eventDidMount={(info) => {
                const tooltipText = [
                  info.event.title,
                  info.event.extendedProps?.details || '',
                ].filter(Boolean).join(' - ');
                if (tooltipText) {
                  info.el.setAttribute('title', tooltipText);
                }
                if (info.event.extendedProps?.isHoliday) return;
                info.el.addEventListener('contextmenu', (event) => {
                  event.preventDefault();
                  openEventEditor(info.event, event);
                });
              }}
              drop={handleExternalDrop}
              eventContent={renderEventContent}
              datesSet={handleDatesSet}
              dateClick={handleDateClick}
              eventDragMinDistance={5}
              dragRevertDuration={150}
              dragScroll
              snapDuration="00:15:00"
              allDayMaintainDuration
              eventStartEditable
              eventResizableFromStart
              eventDurationEditable
              eventOverlap
              slotEventOverlap
              eventAllow={() => true}
              dropAccept=".external-event-item"
              dayCellClassNames={(info) => {
                const dateKey = toLocalDateKey(info.date);
                const classNames = [];
                if (config.overrides[dateKey]?.enabled) classNames.push('has-override');
                if (holidaySettings.markNonWorkingDays && holidayLookup.has(dateKey)) classNames.push('has-holiday');
                return classNames;
              }}
              dayCellContent={(info) => {
                const dateKey = toLocalDateKey(info.date);
                const hasOverride = config.overrides[dateKey]?.enabled;
                const holidayItems = holidayLookup.get(dateKey) || [];
                const weekday = info.date.toLocaleDateString('en-US', { weekday: 'short' });

                return (
                  <div className="fc-daycell-inner">
                    <span className="fc-daygrid-day-number">{info.dayNumberText}</span>
                    <span className="fc-daygrid-day-weekday">{weekday}</span>
                    {holidayItems.length > 0 ? (
                      <span className="fc-day-holiday-label" title={holidayItems.map((item) => item.name).join(', ')}>
                        {holidayItems[0].name}
                      </span>
                    ) : null}
                    {hasOverride ? <span className="fc-day-override-dot" title="Has override">•</span> : null}
                  </div>
                );
              }}
              eventClassNames={(arg) => {
                const day = arg.event.start?.getDay?.();
                return day === 0 || day === 6 ? ['fc-event-weekend'] : [];
              }}
              noEventsContent="No events in current view range"
              dayMaxEventRows
              eventMaxStack={4}
              moreLinkClick="popover"
              dayPopoverFormat={{ month: 'short', day: 'numeric', weekday: 'short' }}
              moreLinkClassNames={() => ['rule-add']}
              moreLinkContent={(arg) => `+${arg.num} more`}
              height="auto"
              contentHeight="auto"
              expandRows
              stickyHeaderDates
            />
            {editingEvent ? (
              <div
                className="calendar-event-editor"
                style={{
                  left: `${editingEvent.position.left}px`,
                  top: `${editingEvent.position.top}px`,
                }}
              >
                <div
                  className="calendar-event-editor-header"
                  onMouseDown={(event) => {
                    const bounds = event.currentTarget.parentElement?.getBoundingClientRect();
                    if (!bounds) return;
                    setEditorDrag({
                      offsetX: event.clientX - bounds.left,
                      offsetY: event.clientY - bounds.top,
                    });
                  }}
                >
                  <strong>Edit Event</strong>
                  <button
                    type="button"
                    className="calendar-event-editor-close"
                    onClick={() => {
                      setEditorDrag(null);
                      setEditingEvent(null);
                    }}
                  >
                    x
                  </button>
                </div>
                <div className="calendar-event-editor-form">
                  <label>
                    <span>Title</span>
                    <input
                      type="text"
                      value={editingEvent.title}
                      onChange={(e) => handleEditorChange('title', e.target.value)}
                    />
                  </label>
                  <label>
                    <span>Details</span>
                    <input
                      type="text"
                      value={editingEvent.details}
                      onChange={(e) => handleEditorChange('details', e.target.value)}
                    />
                  </label>
                  <label>
                    <span>Type</span>
                    <select
                      value={editingEvent.type}
                      onChange={(e) => handleEditorChange('type', e.target.value)}
                    >
                      <option value={BLOCK_TYPES.WORK}>Work</option>
                      <option value={BLOCK_TYPES.BREAK}>Break</option>
                    </select>
                  </label>
                  <label>
                    <span>Date</span>
                    <div className="calendar-event-editor-date-fields">
                      <select
                        value={String(editingDateParts?.day ?? 1).padStart(2, '0')}
                        onChange={(e) => handleEditingDatePartChange('day', e.target.value)}
                      >
                        {editingDayOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={String(editingDateParts?.month ?? 1).padStart(2, '0')}
                        onChange={(e) => handleEditingDatePartChange('month', e.target.value)}
                      >
                        {monthOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={String(editingDateParts?.year ?? new Date().getFullYear())}
                        onChange={(e) => handleEditingDatePartChange('year', e.target.value)}
                      >
                        {editingYearOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </label>
                  <div className="calendar-event-editor-times">
                    <label>
                      <span>Start</span>
                      <select
                        value={editingEvent.startTime}
                        onChange={(e) => handleEditorChange('startTime', e.target.value)}
                      >
                        {TIME_OPTIONS.map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>End</span>
                      <select
                        value={editingEvent.endTime}
                        onChange={(e) => handleEditorChange('endTime', e.target.value)}
                      >
                        {TIME_OPTIONS.map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="calendar-event-editor-actions">
                    <button type="button" className="rule-remove" onClick={handleDeleteEditedEvent}>
                      Delete
                    </button>
                    <button type="button" className="quick-event-create" onClick={handleSaveEditedEvent}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </main>
      </div>
    </div>
  );
};

const ResourceCalendar = (props) => (
  <ResourceCalendarErrorBoundary>
    <ResourceCalendarContent {...props} />
  </ResourceCalendarErrorBoundary>
);

export default ResourceCalendar;

